import { useEffect, useMemo, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useBrandInfo } from "@/hooks/useCompanyInfo";
import { useShipmentLiveStatus } from "@/hooks/useShipmentLiveStatus";
import { useSpace } from "@/hooks/useSpace";
import { isHomeKey, menuPath, navFor, type NavEntry, type NavGroup } from "@/domain/menus";
import { isTenantKey } from "@/domain/space";
import { BrandMarkGlyph } from "./BrandMark";
import { pickLogo } from "@/domain/brand_logo";
import { BrandLogo } from "@/components/ui/BrandLogo";

export type SidebarTheme = "dark" | "light";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  theme?: SidebarTheme;
  onToggleMobile?: () => void;
  /** NavLink 클릭 시 호출 — 모바일에서 navigation 후 사이드바 닫기 용도 */
  onItemClick?: () => void;
  /** 고정핀(PC). false 면 64px 레일 + 오버레이. 기본 true(옛 동작) */
  pinned?: boolean;
  /** 핀 토글 — 없으면 핀 버튼을 렌더하지 않음(기능 OFF) */
  onTogglePinned?: () => void;
  /** 핀 OFF hover 열기/닫기 (Layout 이 지연 타이머 관리) */
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  theme = "dark",
  onToggleMobile,
  onItemClick,
  pinned = true,
  onTogglePinned,
  onHoverIn,
  onHoverOut,
}: SidebarProps) {
  // 로고·그룹명 = 루트 브랜드(법인 회사정보와 별개, 12 §9-1 ③)
  const { info } = useBrandInfo();
  // 좌측바 테마(dark/light)에 맞는 전체형 로고. 빈 칸은 pickLogo 가 다른 칸으로 대체
  const brandLogo = pickLogo(info.logos, "full", theme);
  // B′: 사이드바 목록 = 지금 공간의 메뉴(navFor). 권한 필터는 아래에서 기존 gate 그대로.
  // 권한은 법인 단위 판정(hasPermIn — 지금 법인 기준, 1단계는 소속 + 계정 권한)
  const { space, biz, sidebarTint, hasPermIn, canManageTenant } = useSpace();
  // 법인 색조 실험 — 법인 공간 + 토글 켬일 때만 .is-tinted (내 업무는 중립 그대로)
  const tintCls = space !== "my" && sidebarTint ? ` is-tinted is-space-${space}` : "";
  // 출하조회 라이브 마커 점등 — 오늘 출하 시작~마무리 사이에만 초록(링), 그 외 회색
  const liveStatus = useShipmentLiveStatus();

  // 레일(PC 접힘 · fold 64px) 툴팁 — 라벨이 숨겨져 있을 때만 항목 오른쪽에 메뉴명을 띄움.
  // sb-nav 가 overflow 라 CSS ::after 툴팁은 잘리므로 body portal 로 렌더(HoverTip 과 동일 방식).
  // native title 은 지연이 길고 시스템 스타일이라 쓰지 않음.
  const [railTip, setRailTip] = useState<{ label: string; x: number; y: number } | null>(null);
  const hideRailTip = () => setRailTip(null);
  const showRailTip = (e: ReactPointerEvent<HTMLAnchorElement>, label: string) => {
    if (e.pointerType !== "mouse") return; // 터치는 hover 개념이 없음
    // 핀 OFF PC 레일은 hover 로 메뉴가 펼쳐져 라벨이 곧 보이므로 툴팁 중복 → 띄우지 않음
    if (!pinned && window.matchMedia("(min-width: 1024px)").matches) return;
    const el = e.currentTarget;
    // 라벨이 실제로 보이면(펼침·fold 드로어 열림·MO 드로어) 중복이라 띄우지 않음.
    // 접힘 레일에서 라벨은 display:none 이 아니라 opacity:0 으로 남아 있으므로(폭과 함께 fade)
    // offsetParent 만으로는 "보인다"고 오판한다 → 계산된 opacity 까지 확인.
    const lbl = el.querySelector<HTMLElement>(".sb-item-label");
    if (lbl && lbl.offsetParent !== null && Number(getComputedStyle(lbl).opacity) > 0.01) return;
    const r = el.getBoundingClientRect();
    // x 기준은 항목이 아니라 사이드바 오른쪽 가장자리 — 항목 오른쪽(54px)+10 은 레일 폭(64px)과 겹쳐
    // 꼬리(::before, -4px)가 사이드바 안에 묻혀 안 보였음. 가장자리 +12 로 두면 꼬리가 온전히 드러남.
    const aside = el.closest<HTMLElement>(".rmc-sidebar");
    const edge = aside ? aside.getBoundingClientRect().right : r.right;
    setRailTip({ label, x: edge + 12, y: r.top + r.height / 2 });
  };
  // 창 크기 변경 시 좌표가 어긋나므로 닫음 (접힘↔펼침 토글은 aside 의 pointerdown 캡처가 닫아 줌)
  useEffect(() => {
    if (!railTip) return;
    const onResize = () => setRailTip(null);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [railTip]);

  // 메뉴권한 필터: 게이트 없는 메뉴는 항상, 있으면 anyOf 보유 시에만.
  // 항목이 하나도 안 보이는 그룹 헤더는 함께 숨긴다.
  const items = useMemo(() => {
    const out: NavEntry[] = [];
    let pendingGroup: NavGroup | null = null;
    for (const n of navFor(space, biz)) {
      if (n.type === "group") {
        pendingGroup = n;
        continue;
      }
      // 법인 설정(환경설정) = 전체 admin · 그 법인 관리자만(12 §8-A·§8-C) — settings.view 권한 게이트 대신
      const ok =
        n.key === "settings"
          ? isTenantKey(space) && canManageTenant(space)
          : !n.gate || n.gate.some((k) => hasPermIn(k));
      if (!ok) continue;
      if (pendingGroup) {
        out.push(pendingGroup);
        pendingGroup = null;
      }
      // 사이드바 › 시스템 › "법인 설정"(와이어프레임 A 메모 ②). 페이지 제목은 "환경설정" 그대로
      out.push(n.key === "settings" ? { ...n, label: "법인 설정" } : n);
    }
    return out;
  }, [hasPermIn, canManageTenant, space, biz]);

  return (
    <aside
      className={`rmc-sidebar ${theme} ${collapsed ? "is-collapsed" : ""}${tintCls}`}
      // 햄버거 등 사이드바 안 어디를 누르든 레일 툴팁은 닫음(드로어 열림 뒤 잔상 방지)
      onPointerDownCapture={hideRailTip}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") onHoverIn?.(); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") onHoverOut?.(); }}
    >
      <div className="sb-top">
        <button
          className="sb-burger"
          onClick={() => {
            // fold/MO 드로어, 또는 PC 핀 OFF 오버레이 → mobileNavOpen 토글. PC 핀 ON → push 접힘 토글
            if (window.matchMedia("(max-width: 1023px)").matches || !pinned) {
              onToggleMobile?.();
            } else {
              onToggleCollapsed();
            }
          }}
          aria-label="사이드바 토글"
        >
          <Icon name="menu" size={18} />
        </button>
        {/* 접힘 시에도 DOM 에 남겨 폭 트랜지션과 같은 타이밍으로 fade — 조건부 렌더면 로고만
            먼저 사라지고/튀어나와 사이드바 폭 애니메이션과 속도가 어긋나 보인다 */}
        <a
          className={`sb-brand${brandLogo ? " has-logo" : ""}`}
          href="#"
          aria-hidden={collapsed || undefined}
          tabIndex={collapsed ? -1 : undefined}
        >
          {brandLogo ? (
            <BrandLogo className="sb-brand-logo" src={brandLogo} alt={info.companyName || "회사 로고"} />
          ) : (
            <>
              <span className="sb-brand-mark">
                <BrandMarkGlyph />
              </span>
              <span className="sb-brand-text">
                <span className="sb-brand-name">JG ERP</span>
                <span className="sb-brand-sub">중앙경원 통합관리</span>
              </span>
            </>
          )}
        </a>
        {onTogglePinned && (
          <button
            className={`sb-pin ${pinned ? "is-on" : ""}`}
            onClick={onTogglePinned}
            aria-label={pinned ? "사이드바 고정 해제" : "사이드바 고정"}
            aria-pressed={pinned}
            aria-hidden={collapsed || undefined}
            tabIndex={collapsed ? -1 : undefined}
            title={pinned ? "고정 해제 — 레일로 접고 마우스를 대면 펼침" : "고정 — 항상 펼친 채 본문을 밀어냄"}
          >
            <Icon name={pinned ? "pin" : "pinOff"} size={16} />
          </button>
        )}
        <button className="sb-close-mobile" onClick={onToggleMobile} aria-label="닫기" style={{ display: "none" }}>
          <Icon name="close" size={18} />
        </button>
      </div>
      <nav className="sb-nav" onScroll={hideRailTip}>
        {items.map((n, i) => {
          if (n.type === "group" && !n.label) {
            return <div key={i} className="sb-divider" role="separator" />;
          }
          if (n.type === "group") {
            return (
              <div key={i} className="sb-group">
                <span className="sb-group-text">{n.label}</span>
              </div>
            );
          }
          return (
            <NavLink
              key={n.key}
              to={menuPath(n.key, space, biz)}
              end={isHomeKey(n.key)}
              className={({ isActive }) => `sb-item ${isActive ? "is-active" : ""}`}
              onClick={() => { hideRailTip(); onItemClick?.(); }}
              onPointerEnter={(e) => showRailTip(e, n.label)}
              onPointerLeave={hideRailTip}
              // 레일에서는 라벨 span 이 없거나 숨겨져 접근 가능한 이름이 없음 → aria-label 로 보장
              aria-label={n.label}
            >
              {({ isActive }) => (
                <>
                  <span className="sb-item-icon"><Icon name={n.icon} size={18} /></span>
                  {/* 라벨·마커도 DOM 에 남기고 CSS 가 fade — 조건부 렌더면 폭 전환(--sb-anim)과 어긋난다 */}
                  <span
                    className="sb-item-label"
                    style={n.marker === "live" ? { flex: "0 0 auto" } : undefined}
                  >
                    {n.label}
                  </span>
                  {n.marker === "live" ? (
                    <span
                      className={`sb-item-marker sb-item-live ${liveStatus === "live" ? "is-on" : "is-off"}`}
                      title={liveStatus === "live" ? "출하 진행 중" : "출하 대기 중"}
                    >
                      <span className="sb-item-live-dot" aria-hidden />
                      라이브
                    </span>
                  ) : n.marker ? (
                    <span
                      className="sb-item-marker"
                      style={{
                        marginLeft: "auto",
                        fontSize: "var(--fs-2xs)",
                        color: "var(--c-text-muted)",
                      }}
                    >
                      {n.marker}
                    </span>
                  ) : null}
                  {isActive && <span className="sb-item-bar" />}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
      {railTip && typeof document !== "undefined"
        ? createPortal(
            <div
              className="hovertip hovertip--right"
              role="tooltip"
              style={{ left: railTip.x, top: railTip.y }}
            >
              {railTip.label}
            </div>,
            document.body,
          )
        : null}
    </aside>
  );
}
